import React, { useEffect, useState, Fragment } from 'react';
import { getMessage } from 'util/locale';
import { connect } from 'react-redux';
import propTypes from 'prop-types';
import classNames from 'classnames';
import { Dropdown, Label } from '@tcc/crate';

import { APPLICATION_FORMAT, selectIdPrefix } from 'constants/values';

// constants
import { applicationTypes } from 'constants/scripts';

// components
import { ApplicationFormatToggle } from 'planting-plan-form/CropProtection/ApplicationFormatToggle';

// selectors
import { getActiveScript } from 'current/selectors';
import {
  getCurrentPath,
  isEditRoute as isEditRouteFunc,
  isManualRoute
} from 'routes/selectors';
import { 
  getSubstanceTypesForApplicationType,
  getChemicalTypeUuidByApplicationType
} from '../products/fertility-selectors';

// actions
import {
  selectChemical,
  selectCustomChemical,
  fetchCropProtectionProducts
} from './index';

// util
import { formatSelectOptionsFromFertilizerProducts } from 'products/productOptionsUtil';
import { getProductsForScript } from '../scripts/selectors';
import { getPlantingPlanParams } from '../planting-plan-form/selectors';
import { getProductById } from './selectors';

const baseString = 'vra-fertilizer-product-inputs-container';
const selectId = `${selectIdPrefix}1`;

export const FertilizerProductInputsContainer = ({
  allFertProducts,
  createCustomChemical,
  currentProduct,
  chemicalTypeIds,
  chemicalTypeUuid,
  disabled,
  fetchFertilizers,
  isEditRoute,
  isManualRoute,
  onSelectFertilizerProduct,
  options,
  substanceTypeUuid
}) => {
  const classString = classNames({
    [`${baseString}`]: true,
    'vra-planting-plan-form__fieldset': isManualRoute
  });

  const [state, setState] = useState({
    isLoading: false,
    matterState: APPLICATION_FORMAT.spread,
    dropdownValue: currentProduct && currentProduct.name,
    searchDefaultOption: undefined
  });

  useEffect(() => {
    fetchFertilizers(chemicalTypeIds);
  }, []);

  useEffect(() => {
    setState((oldState) => ({
      ...oldState,
      dropdownValue: undefined
    }));
  }, [substanceTypeUuid]);

  useEffect(() => {
    if (currentProduct && currentProduct.matterState === 'liquid') {
      setState((oldState) => ({
        ...oldState,
        matterState: APPLICATION_FORMAT.sprayed
      }));
    } else if (currentProduct) {
      setState((oldState) => ({
        ...oldState,
        matterState: APPLICATION_FORMAT.spread
      }));
    }
  }, [currentProduct]);

  let dropdownProps = {
    disabled,
    isLoading: state.isLoading,
    label: getMessage('fertility-product'),
    options: options.filter((option) => {
      if (
        state.matterState === APPLICATION_FORMAT.sprayed
        && option.matterState === 'liquid'
      ) {
        return true;
      } else if (
        state.matterState === APPLICATION_FORMAT.spread
        && (option.matterState === 'solid' || !option.matterState)
      ) {
        return true;
      }
      return false;
    }),
    placeholder: getMessage('placeholder-text__select-fertilizer'),
    searchable: true,
    selectedId: currentProduct && currentProduct.id,
    setValue: (value, id) => {
      onSelectFertilizerProduct({ currentProductId: id, selectId });
    },
    testId: `${baseString}-1`,
    type: 'Dropdown',
    value: state.dropdownValue,
    optionsCanWrap: false,
    onSearchFilter: (filterString) => {
      const anyOption = allFertProducts.find((option) => {
        return option.name
        .toLowerCase()
        .includes(filterString.trim().toLowerCase());
      });

      const listDisplayText = getMessage('text__use-custom-fertilizer', {
        fertilizerName: filterString
      });
      const option
          = !anyOption && filterString !== ''
            ? { value: filterString, id: 'custom-id', listDisplayText }
            : undefined;
      setState((state) => ({ ...state, searchDefaultOption: option }));
    },
    searchDefaultOption: state.searchDefaultOption,
    setValue: (value, id) => {
      if (id === 'custom-id') {
        createCustomChemical({
          chemicalName: value,
          substanceId: substanceTypeUuid,
          chemicalTypeId: chemicalTypeUuid,
          specificMatterState:
              state.matterState === APPLICATION_FORMAT.spread
                ? 'solid'
                : 'liquid'
        });
        setState((state) => ({ ...state, searchDefaultOption: undefined }));
      } else {
        onSelectFertilizerProduct({ currentProductId: id, selectId });
      }
    }
  };

  let isCustom = false;
  if (
    dropdownProps.selectedId
    && dropdownProps.options.find(
      (opt) => opt.id === dropdownProps.selectedId && opt.isCustom
    )
  ) {
    isCustom = true;
  }

  function setMatterState(matter) {
    setState((oldState) => ({
      ...oldState,
      matterState: matter
    }));
  }

  return (
    <Fragment>
      <section>
        <ApplicationFormatToggle
          isEditRoute={isEditRoute}
          onSelect={setMatterState}
          selected={state.matterState}
        />
      </section>
      <section className={classString}>
        <Dropdown {...dropdownProps} />
        {isCustom ? (
          <Label
            className={`custom-chemical-inline-badge ${
              isManualRoute ? 'custom-chemical-inline-fert-create' : ''
            }`}
            status="default"
            testId="crate-label-success-primary"
          >
            {getMessage('text__use-custom-label-abbrev')}
          </Label>
        ) : null}
      </section>
    </Fragment>
  );
};
FertilizerProductInputsContainer.propTypes = {
  allFertProducts: propTypes.array.isRequired,
  chemicalTypeIds: propTypes.array,
  chemicalTypeUuid: propTypes.string,
  createCustomChemical: propTypes.func,
  currentProduct: propTypes.object,
  disabled: propTypes.bool,
  fetchFertilizers: propTypes.func,
  isEditRoute: propTypes.bool,
  isManualRoute: propTypes.bool,
  onSelectFertilizerProduct: propTypes.func,
  options: propTypes.array.isRequired,
  substanceTypeUuid: propTypes.string.isRequired
};

export default connect(
  (state, props) => {
    const currentPath = getCurrentPath(state);
    const isEditRoute = isEditRouteFunc(currentPath);
    // TODO Change in PRDEN-6 Phase 2 when scripts can have multiple products

    const products
      = state.products[applicationTypes.FERTILIZER].productsBySubstanceTypeUuid[
      props.substanceTypeUuid
      ] || [];

    const allFertProducts = []; // we combine chemicals from both FERTILITY and CROP PROTECTION
    const productsBySubstanceTypeUuid
      = state.products[applicationTypes.FERTILIZER].productsBySubstanceTypeUuid;
    const chemicalTypeIds = getSubstanceTypesForApplicationType(
      state,
      applicationTypes.APPLICATION
    ).byName;
    const cpProductsByChemicalTypeUuid
      = state.products[applicationTypes.APPLICATION].productsByChemicalTypeUuid;
    Object.values(cpProductsByChemicalTypeUuid).forEach(
      (prodByChemicalTypeUuid) => {
        prodByChemicalTypeUuid.forEach((prod) => {
          allFertProducts.push(prod);
        });
      }
    );

    if (productsBySubstanceTypeUuid) {
      Object.values(productsBySubstanceTypeUuid).forEach((substanceTypeId) => {
        substanceTypeId.forEach((substanceId) => {
          allFertProducts.push(substanceId);
        });
      });
    }
    const currentProduct = isEditRoute
      ? getProductsForScript(getActiveScript(state))[0]
      : getProductById({
        state,
        applicationType: applicationTypes.FERTILIZER,
        id: getPlantingPlanParams(state).products.fertilizer[selectId]
      });
    return {
      allFertProducts,
      currentProduct,
      chemicalTypeIds,
      chemicalTypeUuid: getChemicalTypeUuidByApplicationType(
        state,
        applicationTypes.FERTILIZER
      ),
      isEditRoute,
      isManualRoute: isManualRoute(currentPath),
      options: formatSelectOptionsFromFertilizerProducts(products)
    };
  },
  (dispatch, props) => {
    return {
      fetchFertilizers: (chemicalTypeIds) => {
        dispatch(
          fetchCropProtectionProducts({
            chemicalTypeIds
          })
        );
      },
      onSelectFertilizerProduct: ({ currentProductId, selectId }) => {
        dispatch(selectChemical({ currentProductId, selectId }));

        if (typeof props.onSelectFertilizerProduct === 'function') {
          props.onSelectFertilizerProduct({ currentProductId, selectId });
        }
      },
      createCustomChemical: ({
        chemicalName,
        chemicalTypeId,
        substanceId,
        specificMatterState
      }) => {
        // todo bindActionCreators
        dispatch(
          selectCustomChemical({
            chemicalName,
            chemicalTypeId,
            substanceId,
            specificMatterState,
            selectId
          })
        );
      }
    };
  }
)(FertilizerProductInputsContainer);
